// src/App.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { createChart } from 'lightweight-charts';
import * as XLSX from 'xlsx';
import {
    Box, useTheme, TextField, Button, IconButton, Chip,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import {
    ArrowUpward, ArrowDownward, UnfoldMore as UnfoldMoreIcon, UploadFile as UploadFileIcon,
    ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon,
    SkipPrevious as SkipPreviousIcon, SkipNext as SkipNextIcon, ZoomOutMap as ZoomOutMapIcon,
    Close as CloseIcon, Add as AddIcon, StarBorder as StarBorderIcon,
} from '@mui/icons-material';

import { tokens } from "../../theme";
import TradingViewTable from '../../components/TradingViewTable';

import './outshort.css';

import { __fetching_data__ } from '../../components/api';

/* ------------------------------ Utilities ------------------------------ */
const PALETTE_19 = [
    '#4E79A7', '#F28E2B', '#59A14F', '#E15759', '#9C755F',
    '#76B7B2', '#EDC948', '#B07AA1', '#FF9DA7', '#BAB0AC',
    '#1F77B4', '#FF7F0E', '#2CA02C', '#D62728', '#9467BD',
    '#8C564B', '#E377C2', '#BCBD22', '#17BECF',
];

const makeColorMap = (names) => {
    const map = new Map();
    names.forEach((n, i) => map.set(n, PALETTE_19[i % PALETTE_19.length]));
    return map;
};
const toBusinessDay = (yyyy_mm_dd) => ({
    year: +yyyy_mm_dd.slice(0, 4),
    month: +yyyy_mm_dd.slice(5, 7),
    day: +yyyy_mm_dd.slice(8, 10),
});
const buildSeriesData = (name, chartData) => {
    const obj = chartData?.[name];
    if (!obj) return [];
    return Object.entries(obj)
        .filter(([d]) => /^\d{4}-\d{2}-\d{2}$/.test(d))
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([d, val]) => ({ time: toBusinessDay(d), value: Number(val) }));
};

/* ------------------------------ Free float API ------------------------------ */
const base_api_url = process.env.NODE_ENV === "production"
    ? window.location.protocol === "https:"
        ? process.env.REACT_APP_API_HTTPS_URL
        : process.env.REACT_APP_API_HTTP_URL
    : "/application";

const FREEFLOAT_API_BASE = `${base_api_url}/freefloat`;

// GET http://localhost:3006/freefloat?symbol=AOT
// {
//   status, code,
//   data: {
//     updated_at, execution_time_sec,
//     rows: [ ["2026-09-10", 62.25, 30], ... ]   // [date, priceValue, freeFloatValue]
//   }
// }
async function fetchFreeFloatSeries(symbol) {
    const url = `${FREEFLOAT_API_BASE}?symbol=${encodeURIComponent(symbol)}`;
    // __fetching_data__ returns the already-parsed JSON body directly — it is
    // NOT a fetch() Response, so there is no .ok / .json() to call here.
    const json = await __fetching_data__({ url, method: "GET" });
    if (!json?.status) throw new Error(`API returned status=false for ${symbol}`);

    const rows = Array.isArray(json?.data?.rows) ? json.data.rows : [];
    const freeFloatByDate = {};
    const priceByDate = {}; // keyed by date so duplicate rows (same date appearing
                             // more than once in the raw response) collapse to one —
                             // lightweight-charts requires unique, ascending timestamps

    for (const row of rows) {
        const [date, priceValue, freeFloatValue] = row || [];
        if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) continue;

        if (Number.isFinite(Number(freeFloatValue))) {
            freeFloatByDate[date] = Number(freeFloatValue);
        }
        if (Number.isFinite(Number(priceValue))) {
            priceByDate[date] = Number(priceValue);
        }
    }

    const priceSeries = Object.entries(priceByDate)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([date, value]) => ({ time: toBusinessDay(date), value }));

    return { freeFloatByDate, priceSeries };
}

const FREEFLOATALLMIN_API_BASE = `${base_api_url}/freefloatallmin`;

// GET http://localhost:3006/freefloatallmin?symbol=DTCI,PTT,CPALL,AOT
// {
//   rows: [
//     { "symbol": "AOT", "min_free_float": 1234.5 },
//     ...
//   ]
// }
// Lightweight — called once right after upload just to fill in the table's
// Free Float column for every symbol quickly. The full time series used by
// the chart is still lazy-loaded per symbol via /freefloat when selected.
async function fetchFreeFloatMin(symbols) {
    const query = symbols.map(s => encodeURIComponent(s)).join(',');
    const url = `${FREEFLOATALLMIN_API_BASE}?symbol=${query}`;
    const res = await fetch(url, { headers: { 'Content-Type': 'application/json' } });
    if (!res.ok) throw new Error(`HTTP ${res.status} for freefloatallmin`);
    const json = await res.json();

    // Accept rows either at the top level or nested under `data`.
    const rows = Array.isArray(json?.rows)
        ? json.rows
        : Array.isArray(json?.data?.rows)
            ? json.data.rows
            : [];

    const minBySymbol = {};
    for (const row of rows) {
        const sym = row?.symbol;
        const value = row?.min_free_float;
        if (typeof sym === 'string' && Number.isFinite(Number(value))) {
            minBySymbol[sym] = Number(value);
        }
    }
    return minBySymbol;
}

/* ------------------------------ Favorites API ------------------------------ */
const FAVORITE_SAVE_API = `${base_api_url}/freefloat/favorite/save`;
const FAVORITE_LIST_API = `${base_api_url}/freefloat/favorite/list`;

// POST /freefloat/favorite/save
// body: { user_id, favorite_name, symbols: [...] }
async function saveFavoriteApi(userId, favoriteName, symbols) {
    // __fetching_data__ returns parsed JSON directly — no .ok / .json() here.
    const json = await __fetching_data__({
        url: FAVORITE_SAVE_API,
        body: JSON.stringify({ user_id: userId, favorite_name: favoriteName, symbols }),
        headers: { 'Content-Type': 'application/json' },
        method: "POST",
    });
    return json;
}

// GET /freefloat/favorite/list
// { status, code, data: { ok, rows: [ { favorite_id, user_id, favorite_name, symbols: [...] }, ... ] } }
async function fetchFavoritesApi() {
    const json = await __fetching_data__({
        url: FAVORITE_LIST_API,
        method: "GET",
    });
    return Array.isArray(json?.rows)
        ? json.rows
        : Array.isArray(json?.data?.rows)
            ? json.data.rows
            : [];
}

/* ------------------------------ LineChart ------------------------------ */
function LineChart({ seriesList = [], suppressLabels = false, activeSymbol }) {
    const containerRef = useRef(null);
    const chartRef = useRef(null);
    const seriesMap = useRef(new Map());
    const theme = useTheme();

    useEffect(() => {
        const el = containerRef.current;
        const chart = createChart(el, {
            width: el.clientWidth,
            height: 800,
            layout: {
                attributionLogo: false,
                background: { type: 'solid', color: theme.palette.background.default },
                textColor: theme.palette.text.primary,
            },
            rightPriceScale: { visible: true },
            leftPriceScale: { visible: false },
            grid: {
                horzLines: { color: theme.palette.divider },
                vertLines: { color: theme.palette.divider },
            },
            handleScroll: {
                mouseWheel: true,
                pressedMouseMove: true,
            },
            handleScale: {
                axisPressedMouseMove: true,
                mouseWheel: true,
                pinch: true,
            },
            watermark: {
                visible: true,
                text: activeSymbol == null ? '' : activeSymbol,
                fontSize: 24,
                color: 'rgba(192, 192, 192, 0.4)',
                horzAlign: 'center',
                vertAlign: 'center',
            },
            timeScale: {
                rightOffset: 9,
                rightBarStaysOnScroll: true,
                timeVisible: true,
            },
        });
        chartRef.current = chart;

        const ro = new ResizeObserver(() => {
            chart.applyOptions({
                width: el.clientWidth, height: 800,
            });
        });
        ro.observe(el);

        return () => {
            for (const [, s] of Array.from(seriesMap.current.entries())) {
                if (s) {
                    try { chart.removeSeries(s); } catch { }
                }
            }
            seriesMap.current.clear();
            ro.disconnect();
            chart.remove();
            chartRef.current = null;
        };
    }, [activeSymbol]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const map = seriesMap.current;

        const list = Array.isArray(seriesList) ? seriesList : [];

        for (const [name, s] of Array.from(map.entries())) {
            const stillThere = list.some(it => it && it.name === name);
            if (!stillThere) {
                if (s) {
                    try { chart.removeSeries(s); } catch { }
                }
                map.delete(name);
            }
        }

        const wantLabels = !suppressLabels;

        for (const item of list) {
            if (!item) continue;
            const { name, data = [], color, priceScaleId, lineWidth = 2 } = item;
            if (!name) continue;

            let s = map.get(name);
            if (!s) {
                s = chart.addLineSeries({
                    color,
                    lineWidth,
                    priceScaleId: priceScaleId || 'right',
                    title: wantLabels ? name : '',
                    lastValueVisible: wantLabels,
                    priceLineVisible: false,
                });
                map.set(name, s);
            } else {
                s.applyOptions({
                    color,
                    lineWidth,
                    priceScaleId: priceScaleId || 'right',
                    title: wantLabels ? name : '',
                    lastValueVisible: wantLabels,
                    priceLineVisible: false,
                });
            }

            try {
                s.setData(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error(`Failed to set chart data for series "${name}"`, err);
            }
        }
    }, [seriesList, suppressLabels]);

    useEffect(() => {
        const chart = chartRef.current;
        if (!chart) return;
        const list = Array.isArray(seriesList) ? seriesList : [];
        const hasLeft = list.some(s => s && s.priceScaleId === 'left');
        chart.applyOptions({ leftPriceScale: { visible: hasLeft } });
    }, [seriesList]);

    // navigation controls — keep the current zoom (bar span) when jumping to
    // the very first / very last data point
    const goToFirst = () => {
        const chart = chartRef.current;
        if (!chart) return;
        const range = chart.timeScale().getVisibleLogicalRange();
        const span = range ? (range.to - range.from) : 50;
        chart.timeScale().setVisibleLogicalRange({ from: 0, to: span });
    };

    const goToLast = () => {
        const chart = chartRef.current;
        if (!chart) return;
        chart.timeScale().scrollToRealTime();
    };

    const fitAll = () => {
        const chart = chartRef.current;
        if (!chart) return;
        chart.timeScale().fitContent();
    };

    const navButtonSx = {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5, marginBottom: '6px' }}>
                <IconButton size="small" onClick={goToFirst} title="ไปยังข้อมูลตัวแรก" sx={navButtonSx}>
                    <SkipPreviousIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={fitAll} title="Zoom fit ข้อมูลทั้งหมด" sx={navButtonSx}>
                    <ZoomOutMapIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={goToLast} title="ไปยังข้อมูลตัวสุดท้าย" sx={navButtonSx}>
                    <SkipNextIcon fontSize="small" />
                </IconButton>
            </Box>
            <Box
                ref={containerRef}
                sx={{
                    width: '100%',
                    height: 800,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    boxShadow: theme.palette.shadow?.primary || '0 2px 6px rgba(0,0,0,0.1)',
                    overflow: 'hidden',
                }}
            />
        </Box>
    );
}


/* ------------------------------ StockTable ------------------------------ */
function StockTable({
    rows = [],
    selectedSet = new Set(),
    activeSymbol,
    onPick,
    onToggle,
    onNavigate,
    onClearAll,
    getColor = () => '#999',
    height = 800,
    uploadedCount = 0,
    onUploadSymbols,
    onClearUpload,
    onDeleteSymbol,
    onAddSymbol,
    sortBy,
    sortDir,
    onSort,
    favorites = [],
    onSaveFavorite,
    onLoadFavorite,
    isSavingFavorite = false,
    open = true,
    onToggleOpen,
}) {
    const theme = useTheme();

    const clickTimeoutRef = useRef(null);
    const clickedSymbolRef = useRef(null);
    const fileInputRef = useRef(null);
    const rootRef = useRef(null);
    const panelRef = useRef(null);

    const [selected, setSelected] = useState(0);
    const [scrollGutter, setScrollGutter] = useState(0);
    const [addSymbolInput, setAddSymbolInput] = useState('');
    const [saveDialogOpen, setSaveDialogOpen] = useState(false);
    const [favoriteNameInput, setFavoriteNameInput] = useState('');
    const [loadFavoriteId, setLoadFavoriteId] = useState('');
    const listRef = useRef(null);

    const submitAddSymbol = () => {
        const sym = addSymbolInput.trim();
        if (!sym) return;
        onAddSymbol?.(sym);
        setAddSymbolInput('');
    };

    const submitSaveFavorite = () => {
        const name = favoriteNameInput.trim();
        if (!name) return;
        onSaveFavorite?.(name);
        setSaveDialogOpen(false);
        setFavoriteNameInput('');
    };

    // measure the width any inner scrollbar eats up, so the header row (with the
    // upload button) can reserve the same amount and line up with the table below
    useEffect(() => {
        const measure = () => {
            const outerWidth = rootRef.current?.clientWidth || 0;
            const panelWidth = panelRef.current?.clientWidth || outerWidth;
            setScrollGutter(Math.max(outerWidth - panelWidth, 0));
        };
        measure();

        const ro = new ResizeObserver(measure);
        if (rootRef.current) ro.observe(rootRef.current);
        if (panelRef.current) ro.observe(panelRef.current);
        window.addEventListener('resize', measure);
        return () => {
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [rows.length]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setSelected((prev) => Math.min(prev + 1, rows.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setSelected((prev) => Math.max(prev - 1, 0));
            }
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [rows.length]);

    useEffect(() => {
        const activeRow = listRef.current?.querySelector(".selected");
        activeRow?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, [selected]);

    const handleClick = (sym, e, i) => {
        if (e.ctrlKey || e.metaKey) {
            onToggle(sym);
            return;
        }

        if (clickTimeoutRef.current && clickedSymbolRef.current === sym) {
            clearTimeout(clickTimeoutRef.current);
            clickTimeoutRef.current = null;
            clickedSymbolRef.current = null;
            onToggle(sym);
        } else {
            clearTimeout(clickTimeoutRef.current);
            clickedSymbolRef.current = sym;
            clickTimeoutRef.current = setTimeout(() => {
                onPick(sym, e);
                clickTimeoutRef.current = null;
                clickedSymbolRef.current = null;
            }, 250);
        }
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                onNavigate(e.key === 'ArrowUp' ? 'up' : 'down');
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            if (clickTimeoutRef.current) {
                clearTimeout(clickTimeoutRef.current);
            }
        };
    }, [onNavigate]);

    if (!open) {
        return (
            <Box
                ref={rootRef}
                sx={{
                    width: '100%',
                    height,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    overflow: 'hidden',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '6px',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.default,
                        flexShrink: 0,
                    }}
                >
                    <IconButton
                        onClick={onToggleOpen}
                        size="small"
                        title="Show full table"
                        sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                        }}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                </Box>
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    {rows.map(({ sym, value }, i, arr) => {
                        const isOn = selectedSet.has(sym);
                        const isActive = activeSymbol === sym;
                        const color = getColor(sym);
                        const isLastRow = i === arr.length - 1;
                        return (
                            <div
                                key={sym}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => handleClick(sym, e, i)}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(sym, e, i); }}
                                title={sym}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 4,
                                    padding: '3px 6px',
                                    cursor: 'pointer',
                                    boxSizing: 'border-box',
                                    borderBottom: isLastRow ? 'none' : `1px solid ${theme.palette.divider}`,
                                    background: isActive
                                        ? '#7a7a7a'
                                        : (i % 2 === 0
                                            ? theme.palette.table?.odd || theme.palette.background.paper
                                            : theme.palette.table?.even || theme.palette.background.default),
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
                                    <span
                                        style={{
                                            width: 6,
                                            height: 6,
                                            flexShrink: 0,
                                            borderRadius: '50%',
                                            border: `2px solid ${isOn ? color : '#bbb'}`,
                                            background: isOn ? color : 'transparent',
                                        }}
                                    />
                                    <span style={{
                                        fontWeight: 'bold',
                                        color: theme.palette.text.primary,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        {sym}
                                    </span>
                                </div>
                                <div style={{ color: theme.palette.text.primary, flexShrink: 0 }}>
                                    {Number.isFinite(value) ? value.toFixed(2) : '—'}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </Box>
        );
    }

    return (<Box
        ref={rootRef}
        sx={{
            width: '100%',
            minWidth: 0,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            height,
        }}
    >
        <Box
            sx={{
                paddingBottom: "15px",
                paddingRight: `${scrollGutter}px`,
                background: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            <IconButton
                onClick={onToggleOpen}
                size="small"
                title="Hide symbol table"
                sx={{
                    height: 38,
                    width: 38,
                    flexShrink: 0,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                }}
            >
                <ChevronRightIcon fontSize="small" />
            </IconButton>
            <input
                ref={fileInputRef}
                type="file"
                accept=".txt,text/plain,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                style={{ display: 'none' }}
                onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file && onUploadSymbols) onUploadSymbols(file);
                    e.target.value = '';
                }}
            />
            <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon fontSize="small" />}
                onClick={() => fileInputRef.current?.click()}
                sx={{
                    flex: 1,
                    minWidth: 0,
                    height: 38,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    borderRadius: 2,
                    justifyContent: 'flex-start',
                }}
            >
                Upload Symbols
            </Button>
            {false && (
                <Chip
                    size="small"
                    label={`${uploadedCount} loaded`}
                    color="info"
                    onDelete={onClearUpload}
                    sx={{ height: 24, '& .MuiChip-label': { px: 1, fontWeight: 'medium' } }}
                />
            )}
            {false && (
                <Chip
                    size="small"
                    label={selectedSet.size}
                    color="warning"
                    onDelete={onClearAll}
                    sx={{ height: 24, '& .MuiChip-label': { px: 1, fontWeight: 'medium' } }}
                />
            )}
        </Box>

        <Box
            sx={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingRight: `${scrollGutter}px`,
                background: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            <TextField
                placeholder="Add symbol e.g. PTT"
                size="small"
                fullWidth
                value={addSymbolInput}
                onChange={(e) => setAddSymbolInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === 'Enter') submitAddSymbol(); }}
                sx={{ flex: 1 }}
            />
            <IconButton
                onClick={submitAddSymbol}
                size="small"
                color="primary"
                title="Add symbol"
                sx={{
                    height: 38,
                    width: 38,
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`,
                }}
            >
                <AddIcon fontSize="small" />
            </IconButton>
        </Box>

        <Box
            sx={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingRight: `${scrollGutter}px`,
                background: theme.palette.background.default,
                borderBottom: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            <Button
                variant="outlined"
                size="small"
                startIcon={<StarBorderIcon fontSize="small" />}
                onClick={() => setSaveDialogOpen(true)}
                disabled={rows.length === 0}
                sx={{
                    height: 38,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    borderRadius: 2,
                }}
            >
                Save Favorite
            </Button>

            <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                <InputLabel id="load-favorite-label">Load Favorite</InputLabel>
                <Select
                    labelId="load-favorite-label"
                    label="Load Favorite"
                    value={loadFavoriteId}
                    onChange={(e) => {
                        const id = e.target.value;
                        setLoadFavoriteId(id);
                        const fav = favorites.find(f => String(f.favorite_id) === String(id));
                        if (fav) onLoadFavorite?.(fav);
                    }}
                    displayEmpty
                    renderValue={(id) => {
                        const fav = favorites.find(f => String(f.favorite_id) === String(id));
                        return fav ? fav.favorite_name : 'Load Favorite';
                    }}
                >
                    {favorites.length === 0 && (
                        <MenuItem disabled value="">No favorites saved yet</MenuItem>
                    )}
                    {favorites.map(fav => (
                        <MenuItem key={fav.favorite_id} value={fav.favorite_id}>
                            {fav.favorite_name}
                            <span style={{ marginLeft: 8, color: theme.palette.text.secondary, fontSize: 12 }}>
                                ({(fav.symbols || []).length})
                            </span>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>

        <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="xs" fullWidth>
            <DialogTitle>Save Favorite</DialogTitle>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    label="Favorite name"
                    placeholder="e.g. หุ้นที่สนใจ"
                    fullWidth
                    value={favoriteNameInput}
                    onChange={(e) => setFavoriteNameInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitSaveFavorite(); }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={submitSaveFavorite}
                    disabled={!favoriteNameInput.trim() || isSavingFavorite}
                >
                    {isSavingFavorite ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>

        <div
            className="stock-panel"
            ref={panelRef}
            style={{ width: '100%', height: '100%', boxSizing: 'border-box' }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 96px 28px',
                    fontSize: 13,
                    padding: '8px 10px',
                    color: '#666',
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <Box
                    onClick={() => onSort?.('symbol')}
                    title={
                        sortBy === 'symbol'
                            ? (sortDir === 'asc' ? 'เรียงจาก A ไป Z (คลิกเพื่อสลับ)' : 'เรียงจาก Z ไป A (คลิกเพื่อสลับ)')
                            : 'คลิกเพื่อเรียงลำดับตามชื่อ'
                    }
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        color: sortBy === 'symbol' ? '#111' : 'inherit',
                        fontWeight: sortBy === 'symbol' ? 700 : 400,
                        '&:hover': { color: '#111' },
                    }}
                >
                    Symbol
                    {sortBy === 'symbol' ? (
                        sortDir === 'asc'
                            ? <ArrowUpward sx={{ fontSize: 14, color: '#111' }} />
                            : <ArrowDownward sx={{ fontSize: 14, color: '#111' }} />
                    ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 14, color: '#999' }} />
                    )}
                </Box>
                <Box
                    onClick={() => onSort?.('value')}
                    title={
                        sortBy === 'value'
                            ? (sortDir === 'asc' ? 'เรียงจากน้อยไปมาก (คลิกเพื่อสลับ)' : 'เรียงจากมากไปน้อย (คลิกเพื่อสลับ)')
                            : 'คลิกเพื่อเรียงลำดับตาม Free Float'
                    }
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: 0.5,
                        textAlign: 'right',
                        cursor: 'pointer',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        color: sortBy === 'value' ? '#111' : 'inherit',
                        fontWeight: sortBy === 'value' ? 700 : 400,
                        '&:hover': { color: '#111' },
                    }}
                >
                    Free Float
                    {sortBy === 'value' ? (
                        sortDir === 'asc'
                            ? <ArrowUpward sx={{ fontSize: 14, color: '#111' }} />
                            : <ArrowDownward sx={{ fontSize: 14, color: '#111' }} />
                    ) : (
                        <UnfoldMoreIcon sx={{ fontSize: 14, color: '#999' }} />
                    )}
                </Box>
                <div />
            </Box>

            <div
                className="stock-table"
                ref={listRef}
                tabIndex={0}
                style={{ width: '100%', boxSizing: 'border-box', scrollbarGutter: 'stable' }}
            >
                <table style={{ width: '100%' }}>
                    <tbody>
                        {rows.map(({ sym, value }, i, arr) => {
                            const isOn = selectedSet.has(sym);
                            const isActive = activeSymbol === sym;
                            const color = getColor(sym);
                            const isLastRow = i === arr.length - 1;

                            return (<tr
                                key={i}
                                className={`${value < 0 ? "down" : "up"} ${selected === i ? "selected" : ""
                                    }`}
                                onClick={() => setSelected(i)}
                            >
                                <td style={{ padding: 0 }}>
                                    <div
                                        key={sym}
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => handleClick(sym, e, i)}
                                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(sym, e, i); }}
                                        title={isOn ? 'Selected' : 'Select'}
                                        style={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr 96px 28px',
                                            alignItems: 'center',
                                            width: '100%',
                                            padding: '8px 10px',
                                            border: 'none',
                                            outline: 'none',
                                            boxSizing: 'border-box',
                                            borderBottom: isLastRow ? 'none' : `1px solid ${theme.palette.divider}`,
                                            borderBottomLeftRadius: isLastRow ? 2 : 0,
                                            borderBottomRightRadius: isLastRow ? 2 : 0,
                                            background: isActive
                                                ? '#989898'
                                                : (i % 2 === 0
                                                    ? theme.palette.table?.odd || theme.palette.background.paper
                                                    : theme.palette.table?.even || theme.palette.background.default),
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                            <span
                                                style={{
                                                    width: 10,
                                                    height: 10,
                                                    flexShrink: 0,
                                                    borderRadius: '50%',
                                                    border: `2px solid ${isOn ? color : '#bbb'}`,
                                                    background: isOn ? color : 'transparent',
                                                }}
                                            />
                                            <span style={{
                                                fontWeight: 'bold',
                                                color: theme.palette.text.primary,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                {sym}
                                            </span>
                                        </div>
                                        <div style={{
                                            textAlign: 'right',
                                            color: theme.palette.text.primary,
                                            paddingRight: 0
                                        }}>
                                            {Number.isFinite(value) ? value.toFixed(2) : '—'}
                                        </div>
                                        <IconButton
                                            size="small"
                                            title={`Remove ${sym}`}
                                            onClick={(e) => { e.stopPropagation(); onDeleteSymbol?.(sym); }}
                                            sx={{ padding: '2px', color: theme.palette.text.secondary }}
                                        >
                                            <CloseIcon sx={{ fontSize: 16 }} />
                                        </IconButton>
                                    </div>
                                </td>
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    </Box>
    );
}


/* ------------------------------ Main App ------------------------------ */
export default function FreeFloat() {
    const userId = useSelector((state) => state.user);

    // data — starts empty; populated only once the user uploads a symbol list
    const [freeFloatChartData, setFreeFloatChartData] = useState({});
    const [originalFreeFloatChartData, setOriginalFreeFloatChartData] = useState({});
    const [priceChartData, setPriceChartData] = useState({});

    const [uploadedSymbols, setUploadedSymbols] = useState(null); // array of symbols from uploaded file, or null
    const [loadingSymbols, setLoadingSymbols] = useState(() => new Set()); // symbols currently being fetched
    const [loadError, setLoadError] = useState(null);
    const [minFreeFloatBySymbol, setMinFreeFloatBySymbol] = useState({}); // { SYM: number } — quick table fill-in from /freefloatallmin
    const fetchingRef = useRef(new Set()); // symbols with an in-flight request (avoids duplicate fetches)
    const [sortBy, setSortBy] = useState(null); // null = keep upload/file order; 'symbol' | 'value'
    const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
    const [selectedSymbols, setSelectedSymbols] = useState([]);
    const [activeSymbol, setActiveSymbol] = useState(null);
    const [favorites, setFavorites] = useState([]);
    const [isTablePanelOpen, setIsTablePanelOpen] = useState(true);
    const [isSavingFavorite, setIsSavingFavorite] = useState(false);

    // single-select toggles
    const PRICE_COLOR = '#0D6EFD';
    const FREE_FLOAT_SINGLE_COLOR = '#E86A10';
    const [showFreeFloatLine, setShowFreeFloatLine] = useState(true);
    const [showPriceLine, setShowPriceLine] = useState(true);
    const activeSingleSym = selectedSymbols.length === 1 ? selectedSymbols[0] : null;
    useEffect(() => { setShowFreeFloatLine(true); setShowPriceLine(true); }, [activeSingleSym]);

    // load saved favorites once on mount
    useEffect(() => {
        fetchFavoritesApi()
            .then(setFavorites)
            .catch(err => console.error('Failed to load favorites', err));
    }, []);

    const filterAreaStyle = {
        marginTop: '1rem',
        marginLeft: "5rem",
        display: "flex",
        alignItems: "center",
        gap: '10px',
    };

    function formatAsYYYYMMDD(dateObj) {
        if (!(dateObj instanceof Date)) return "";
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    }

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState(() => {
        const today = new Date();
        return formatAsYYYYMMDD(today);
    });

    // derived helpers — table lists every symbol from the uploaded file; free-float
    // values fill in lazily as each symbol gets fetched (see the effect below)
    const allSymbols = useMemo(() => uploadedSymbols || [], [uploadedSymbols]);
    const colorMap = useMemo(() => makeColorMap(allSymbols), [allSymbols]);

    // right table rows: prefer the real latest value from the full series once
    // it's loaded, otherwise fall back to the quick /freefloatallmin value,
    // otherwise '—'
    const rows = useMemo(() => {
        const list = allSymbols.map(sym => {
            const obj = freeFloatChartData[sym] || {};
            const dates = Object.keys(obj).sort();
            const last = dates.length ? Number(obj[dates[dates.length - 1]]) : null;
            const value = Number.isFinite(last)
                ? last
                : (Number.isFinite(minFreeFloatBySymbol[sym]) ? minFreeFloatBySymbol[sym] : null);
            return { sym, value };
        });
        // sortBy === null means "no explicit sort yet" -> keep the order symbols were uploaded in
        if (!sortBy) return list;
        const dir = sortDir === 'asc' ? 1 : -1;
        list.sort((a, b) => {
            if (sortBy === 'symbol') return a.sym.localeCompare(b.sym) * dir;
            const valA = a.value ?? -Infinity;
            const valB = b.value ?? -Infinity;
            return (valA - valB) * dir;
        });
        return list;
    }, [allSymbols, freeFloatChartData, minFreeFloatBySymbol, sortBy, sortDir]);

    // fetch free-float + price data only for the symbol(s) currently selected on the
    // chart, and only the first time they're selected — results are cached so
    // switching back to an already-loaded symbol doesn't refetch it.
    useEffect(() => {
        const toFetch = selectedSymbols.filter(
            sym => !(sym in freeFloatChartData) && !fetchingRef.current.has(sym)
        );
        if (!toFetch.length) return;

        toFetch.forEach(sym => fetchingRef.current.add(sym));
        setLoadingSymbols(prev => {
            const next = new Set(prev);
            toFetch.forEach(sym => next.add(sym));
            return next;
        });

        (async () => {
            const results = await Promise.allSettled(toFetch.map(sym => fetchFreeFloatSeries(sym)));
            const failed = [];

            setFreeFloatChartData(prev => {
                const next = { ...prev };
                results.forEach((r, i) => {
                    next[toFetch[i]] = r.status === 'fulfilled' ? r.value.freeFloatByDate : {};
                });
                return next;
            });
            setOriginalFreeFloatChartData(prev => {
                const next = { ...prev };
                results.forEach((r, i) => {
                    next[toFetch[i]] = r.status === 'fulfilled' ? r.value.freeFloatByDate : {};
                });
                return next;
            });
            setPriceChartData(prev => {
                const next = { ...prev };
                results.forEach((r, i) => {
                    next[toFetch[i]] = r.status === 'fulfilled' ? r.value.priceSeries : [];
                });
                return next;
            });

            results.forEach((r, i) => {
                if (r.status === 'rejected') {
                    console.error(`Failed to load freefloat for ${toFetch[i]}`, r.reason);
                    failed.push(toFetch[i]);
                }
            });
            if (failed.length) setLoadError(`Failed to load: ${failed.join(', ')}`);

            toFetch.forEach(sym => fetchingRef.current.delete(sym));
            setLoadingSymbols(prev => {
                const next = new Set(prev);
                toFetch.forEach(sym => next.delete(sym));
                return next;
            });
        })();
    }, [selectedSymbols, freeFloatChartData]);

    const isLoadingActive = selectedSymbols.some(sym => loadingSymbols.has(sym));

    // build series (single vs multi)
    const seriesList = useMemo(() => {
        const lines = [];
        if (selectedSymbols.length === 1) {
            const sym = selectedSymbols[0];
            const freeFloatData = buildSeriesData(sym, freeFloatChartData);
            const priceData = priceChartData[sym] || [];

            if (showPriceLine && priceData.length) {
                lines.push({
                    name: 'Price',
                    color: PRICE_COLOR,
                    lineWidth: 2,
                    priceScaleId: 'left',
                    data: priceData,
                });
            }
            if (showFreeFloatLine) {
                lines.push({
                    name: 'Free Float',
                    color: FREE_FLOAT_SINGLE_COLOR,
                    lineWidth: 2,
                    priceScaleId: 'right',
                    data: freeFloatData,
                });
            }
        } else {
            for (const name of selectedSymbols) {
                lines.push({
                    name,
                    color: colorMap.get(name),
                    lineWidth: 2,
                    priceScaleId: 'right',
                    data: buildSeriesData(name, freeFloatChartData),
                });
            }
        }
        return lines;
    }, [selectedSymbols, freeFloatChartData, colorMap, priceChartData, showPriceLine, showFreeFloatLine]);

    function betweenData(date) {
        const startDateCompare = new Date(startDate);
        const endDateCompare = new Date(endDate);
        if (date.getTime() >= startDateCompare.getTime() && date.getTime() <= endDateCompare.getTime()) {
            return true;
        } else {
            return false;
        }
    }

    const handleApplyFilter = () => {
        const newFreeFloatChartData = {};
        for (const key in originalFreeFloatChartData) {
            const newData = {};
            for (const keyData in originalFreeFloatChartData[key]) {
                if (betweenData(new Date(keyData))) {
                    newData[keyData] = originalFreeFloatChartData[key][keyData];
                }
            }
            newFreeFloatChartData[key] = newData;
        }
        setFreeFloatChartData(newFreeFloatChartData);
    }

    // table actions
    const clearAll = () => {
        setSelectedSymbols([]);
        setActiveSymbol(null);
    };

    const parseSymbolsFromText = (text) => Array.from(new Set(
        text
            .split(/[\r\n,;\t ]+/)
            .map(s => s.trim().toUpperCase())
            .filter(Boolean)
    ));

    // Reads the first column of the first sheet, one symbol per row (same
    // convention as the .txt upload — one symbol per line).
    const parseSymbolsFromExcel = (arrayBuffer) => {
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        if (!firstSheetName) return [];
        const sheet = workbook.Sheets[firstSheetName];
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });
        const symbols = [];
        for (const row of rows) {
            const cell = row?.[0];
            if (cell === null || cell === undefined || cell === '') continue;
            const sym = String(cell).trim().toUpperCase();
            if (sym) symbols.push(sym);
        }
        return Array.from(new Set(symbols));
    };

    // replaces the entire symbol set (used by file upload and by loading a
    // saved favorite) — resets caches, selects the first symbol, and kicks off
    // the lightweight /freefloatallmin fetch to fill in the table quickly
    const loadSymbolList = (parsed) => {
        if (!parsed || !parsed.length) return;

        setFreeFloatChartData({});
        setOriginalFreeFloatChartData({});
        setPriceChartData({});
        setMinFreeFloatBySymbol({});
        fetchingRef.current.clear();
        setLoadingSymbols(new Set());
        setLoadError(null);

        setUploadedSymbols(parsed);
        setSortBy(null); // fresh list -> reflect its own order until user sorts
        setSortDir('asc');

        // default to the first symbol — the lazy per-symbol effect will fetch
        // its full series via /freefloat as soon as it becomes selected
        const firstSymbol = parsed[0];
        setSelectedSymbols([firstSymbol]);
        setActiveSymbol(firstSymbol);

        // quick, lightweight call so the table's Free Float column shows a
        // real number for every symbol right away, without waiting for each
        // one's full time series to load one-by-one
        fetchFreeFloatMin(parsed)
            .then(minBySymbol => setMinFreeFloatBySymbol(minBySymbol))
            .catch(err => {
                console.error('Failed to load freefloatallmin', err);
            });
    };

    const handleUploadSymbols = (file) => {
        const isExcel = /\.(xlsx|xls)$/i.test(file.name);
        const reader = new FileReader();

        reader.onload = (e) => {
            const parsed = isExcel
                ? parseSymbolsFromExcel(e.target.result)
                : parseSymbolsFromText(String(e.target?.result || ''));
            loadSymbolList(parsed);
        };

        if (isExcel) {
            reader.readAsArrayBuffer(file);
        } else {
            reader.readAsText(file);
        }
    };

    const handleSaveFavorite = async (name) => {
        if (!allSymbols.length) return;
        setIsSavingFavorite(true);
        try {
            await saveFavoriteApi(userId, name, allSymbols);
            const rows = await fetchFavoritesApi();
            setFavorites(rows);
        } catch (err) {
            console.error('Failed to save favorite', err);
            setLoadError('Failed to save favorite');
        } finally {
            setIsSavingFavorite(false);
        }
    };

    const handleLoadFavorite = (favorite) => {
        const parsed = Array.from(new Set(
            (favorite?.symbols || [])
                .map(s => String(s).trim().toUpperCase())
                .filter(Boolean)
        ));
        loadSymbolList(parsed);
    };

    const clearUpload = () => {
        setUploadedSymbols(null);
        setFreeFloatChartData({});
        setOriginalFreeFloatChartData({});
        setPriceChartData({});
        setMinFreeFloatBySymbol({});
        setSelectedSymbols([]);
        setActiveSymbol(null);
        setSortBy(null);
        setSortDir('asc');
        setLoadError(null);
        fetchingRef.current.clear();
        setLoadingSymbols(new Set());
    };

    // remove a single symbol from the list (and any data cached for it)
    const deleteSymbol = (sym) => {
        setUploadedSymbols(prev => (prev || []).filter(s => s !== sym));
        setFreeFloatChartData(prev => {
            const next = { ...prev };
            delete next[sym];
            return next;
        });
        setOriginalFreeFloatChartData(prev => {
            const next = { ...prev };
            delete next[sym];
            return next;
        });
        setPriceChartData(prev => {
            const next = { ...prev };
            delete next[sym];
            return next;
        });
        setMinFreeFloatBySymbol(prev => {
            const next = { ...prev };
            delete next[sym];
            return next;
        });
        setSelectedSymbols(prev => prev.filter(s => s !== sym));
        setActiveSymbol(prev => (prev === sym ? null : prev));
        fetchingRef.current.delete(sym);
        setLoadingSymbols(prev => {
            const next = new Set(prev);
            next.delete(sym);
            return next;
        });
    };

    // add a single symbol typed in by hand — merges into the existing list rather
    // than replacing it, and selects it so its data loads right away
    const addSymbolManually = (rawSym) => {
        const sym = rawSym.trim().toUpperCase();
        if (!sym) return;
        setUploadedSymbols(prev => {
            const list = prev || [];
            if (list.includes(sym)) return list;
            return [...list, sym];
        });
        setSelectedSymbols([sym]);
        setActiveSymbol(sym);
    };

    const pickSymbol = (sym, e) => {
        if (e.ctrlKey || e.metaKey) {
            setSelectedSymbols(prev => (prev.includes(sym) ? prev.filter(n => n !== sym) : [...prev, sym]));
        } else {
            setSelectedSymbols([sym]);
        }
        setActiveSymbol(sym);
    };

    const toggleSymbol = (sym) => {
        setSelectedSymbols(prev => (prev.includes(sym) ? prev.filter(n => n !== sym) : [...prev, sym]));
        setActiveSymbol(sym);
    };

    const navigate = (direction) => {
        if (!visible.length) return;
        const currentIdx = visible.findIndex(r => r.sym === activeSymbol);

        let nextIdx = currentIdx;
        if (currentIdx === -1) {
            nextIdx = 0;
        } else if (direction === 'up') {
            nextIdx = Math.max(0, currentIdx - 1);
        } else if (direction === 'down') {
            nextIdx = Math.min(visible.length - 1, currentIdx + 1);
        }

        const newSym = visible[nextIdx]?.sym;
        if (newSym) {
            setActiveSymbol(newSym);
            setSelectedSymbols([newSym]);
        }
    };

    const toggleSortColumn = (column) => {
        setSortBy(prevBy => {
            if (prevBy !== column) {
                setSortDir('asc');
                return column;
            }
            setSortDir(prevDir => (prevDir === 'asc' ? 'desc' : 'asc'));
            return column;
        });
    };

    const visible = rows;

    /* ------------------------------ UI ------------------------------ */
    return (
        <div style={{ padding: 20, width: '100%', maxWidth: 'none', minWidth: 0, boxSizing: 'border-box', flex: '1 1 auto', alignSelf: 'stretch', fontFamily: 'Inter, system-ui, Arial, sans-serif' }}>
            {/* Title + single-select legend toggles */}
            <div style={filterAreaStyle}>
                <h1 style={{ marginRight: "2rem" }}>Free Float</h1>

                <TextField
                    label="Start Date"
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />

                <TextField
                    label="End Date"
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />

                <Button variant="contained" sx={{
                    backgroundColor: colors.greenChart[50],
                    boxShadow: theme.palette.shadow.primary,
                }}
                    onClick={handleApplyFilter}>
                    Apply Filter
                </Button>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginLeft: '35%', marginTop: '10px', marginBottom: '10px' }}>
                {selectedSymbols.length === 1 && (
                    <div style={{ display: 'flex', gap: 16, fontSize: 14 }}>
                        <label style={legendItemStyle}>
                            <input
                                type="checkbox"
                                checked={showFreeFloatLine}
                                onChange={e => setShowFreeFloatLine(e.target.checked)}
                                style={{ display: 'none' }}
                            />
                            <span style={legendDot(FREE_FLOAT_SINGLE_COLOR, showFreeFloatLine)} />
                            <span>Free Float</span>
                        </label>
                        <label style={legendItemStyle}>
                            <input
                                type="checkbox"
                                checked={showPriceLine}
                                onChange={e => setShowPriceLine(e.target.checked)}
                                style={{ display: 'none' }}
                            />
                            <span style={legendDot(PRICE_COLOR, showPriceLine)} />
                            <span>Price</span>
                        </label>
                    </div>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isTablePanelOpen ? 'minmax(0, 1fr) minmax(220px, 280px)' : 'minmax(0, 1fr) 110px', gap: 8, width: '100%' }}>
                <div>
                    {allSymbols.length > 0 ? (
                        <>
                            <LineChart
                                seriesList={seriesList}
                                suppressLabels={false}
                                activeSymbol={activeSymbol}
                            />
                            {isLoadingActive && (
                                <div style={{ color: theme.palette.text.secondary, fontSize: 13, marginTop: 8 }}>
                                    Loading data…
                                </div>
                            )}
                            {loadError && (
                                <div style={{ color: theme.palette.error.main, fontSize: 13, marginTop: 8 }}>
                                    {loadError}
                                </div>
                            )}
                        </>
                    ) : (
                        <Box
                            sx={{
                                width: '100%',
                                height: 800,
                                borderRadius: 2,
                                border: `1px dashed ${theme.palette.divider}`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 1,
                                color: theme.palette.text.secondary,
                            }}
                        >
                            <UploadFileIcon sx={{ fontSize: 40, opacity: 0.5 }} />
                            <div>Upload a symbol list (.txt or .xlsx) to load data</div>
                        </Box>
                    )}
                </div>

                {/* <TradingViewTable data={visible}/> */}

                <StockTable
                    rows={visible}
                    selectedSet={new Set(selectedSymbols)}
                    activeSymbol={activeSymbol}
                    onPick={pickSymbol}
                    onToggle={toggleSymbol}
                    onNavigate={navigate}
                    onClearAll={clearAll}
                    getColor={(name) => colorMap.get(name)}
                    uploadedCount={uploadedSymbols ? uploadedSymbols.length : 0}
                    onUploadSymbols={handleUploadSymbols}
                    onClearUpload={clearUpload}
                    onDeleteSymbol={deleteSymbol}
                    onAddSymbol={addSymbolManually}
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={toggleSortColumn}
                    favorites={favorites}
                    onSaveFavorite={handleSaveFavorite}
                    onLoadFavorite={handleLoadFavorite}
                    isSavingFavorite={isSavingFavorite}
                    open={isTablePanelOpen}
                    onToggleOpen={() => setIsTablePanelOpen(o => !o)}
                />
            </div>

        </div>
    );
}

/* ------------------------------ Inline styles ------------------------------ */
const btnStyle = {
    fontSize: 12, padding: '4px 8px', border: '1px solid #ddd',
    background: 'white', borderRadius: 6, cursor: 'pointer'
};
const legendItemStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer'
};
const legendDot = (color, on) => ({
    width: 10, height: 10, borderRadius: '50%',
    border: `2px solid ${color}`, background: on ? color : 'transparent'
});